import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, type IssueDetail as Detail, type Frame, type IssueStatus } from "../api";
import { relTime, absTime } from "../time";

function isAppFrame(f: Frame): boolean {
  // Heuristikk: rammer med original (source-mapped) eller egne filer er "app"-kode.
  if (f.original) return !/node_modules/.test(f.original.file);
  return !!f.file && !/node_modules|^\s*$/.test(f.file);
}

function FrameLine({ f }: { f: Frame }) {
  const loc = f.line != null ? `:${f.line}${f.col != null ? `:${f.col}` : ""}` : "";
  return (
    <div className={`frame ${isAppFrame(f) ? "app" : ""}`}>
      <span className="fn">{f.function ?? "(anonym)"}</span>{" "}
      <span className="loc">
        {f.file}
        {loc}
      </span>
      {f.original && (
        <span className="orig">
          ↳ {f.original.file}:{f.original.line}:{f.original.col}
          {f.original.name ? ` (${f.original.name})` : ""}
        </span>
      )}
    </div>
  );
}

const STATUS_ACTIONS: { status: IssueStatus; label: string }[] = [
  { status: "resolved", label: "Marker som løst" },
  { status: "ignored", label: "Ignorer" },
  { status: "unresolved", label: "Gjenåpne" },
];

export function IssueDetail() {
  const { gid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!gid) return;
    api
      .issue(gid)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [gid]);

  useEffect(load, [load]);

  async function setStatus(status: IssueStatus) {
    if (!gid) return;
    await api.setIssueStatus(gid, status);
    load();
  }

  if (loading) return <div className="spin" />;
  if (!data?.group) {
    return (
      <div>
        <span className="backlink" onClick={() => nav("/issues")}>← Tilbake</span>
        <div className="card"><div className="empty">fant ikke feilen</div></div>
      </div>
    );
  }

  const { group, events, affectedUsers, releases } = data;
  const latest = events[0];

  return (
    <div>
      <span className="backlink" onClick={() => nav("/issues")}>← Alle feil</span>

      <div className="page-head" style={{ alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <span className="eyebrow">{group.type ?? "Feil"}</span>
          <h1 className="page-title" style={{ fontSize: 24, wordBreak: "break-word" }}>
            {group.title.replace(new RegExp(`^${group.type}:\\s*`), "")}
          </h1>
          <div className="issue-sub" style={{ marginTop: 6 }}>{group.culprit}</div>
        </div>
        <span className={`pill ${group.status}`}>{group.status}</span>
      </div>

      <div className="row" style={{ marginBottom: 18, gap: 8 }}>
        {STATUS_ACTIONS.filter((a) => a.status !== group.status).map((a) => (
          <button
            key={a.status}
            className={`btn ${a.status === "resolved" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setStatus(a.status)}
          >
            {a.label}
          </button>
        ))}
      </div>

      <div className="card reveal" style={{ marginBottom: 16 }}>
        <div className="meta-grid">
          <div>
            <div className="k">Hendelser</div>
            <div className="v">{new Intl.NumberFormat("nb-NO").format(group.count)}</div>
          </div>
          <div>
            <div className="k">Berørte brukere</div>
            <div className="v">{affectedUsers}</div>
          </div>
          <div>
            <div className="k">Først sett</div>
            <div className="v" style={{ fontSize: 13 }}>{relTime(group.first_seen)}</div>
          </div>
          <div>
            <div className="k">Sist sett</div>
            <div className="v" style={{ fontSize: 13 }}>{relTime(group.last_seen)}</div>
          </div>
        </div>
        {releases.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div className="k" style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--faint)" }}>
              Releases
            </div>
            <div className="row" style={{ gap: 8, marginTop: 6, flexWrap: "wrap" }}>
              {releases.map((r) => (
                <span key={r} className="pill" style={{ color: "var(--blue)" }}>{r}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {latest && (
        <div className="card reveal">
          <h3 className="card-title">
            Siste hendelse
            <span className="mono" style={{ fontSize: 11, color: "var(--faint)" }}>
              {absTime(latest.ts)}
              {latest.environment ? ` · ${latest.environment}` : ""}
            </span>
          </h3>
          {latest.message && (
            <div className="code" style={{ marginBottom: 14, whiteSpace: "normal", color: "var(--coral)" }}>
              {latest.message}
            </div>
          )}
          {latest.frames && latest.frames.length > 0 ? (
            <div className="trace">
              {latest.frames.map((f, i) => (
                <FrameLine key={i} f={f} />
              ))}
            </div>
          ) : (
            <div className="empty">ingen stack trace</div>
          )}
          {latest.url && (
            <div className="issue-sub" style={{ marginTop: 12 }}>URL: {latest.url}</div>
          )}
        </div>
      )}
    </div>
  );
}
